"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { getPosts, Post } from "@/lib/community";
import { Pencil, ThumbsUp, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { createClient } from "@/utils/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Image from "next/image";
import { Badge } from "@/components/ui/badge";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// 스크립트 태그 제거 함수 (파일 상단으로 이동)
const removeScriptTags = (html: string): string => {
  if (!html) return "";
  return html.replace(/<script[\s\S]*?<\/script>/gi, "");
};

// Board 정보 타입 (chn_list에 따른 정의)
interface BoardInfo {
  id: string;       // 게시판 이름 (이자 아이디)
  title: string;    // 실제 헤더에 노출될 타이틀
  type: string;     // 게시판 타입 (예: table, gallery, webzine)
  permission: string; // 게시판 권한
}

export default function BBSPage() {
  // URL 파라미터에서 board 값을 추출하고 디코딩합니다.
  const { board } = useParams();
  const decodedBoard = board ? decodeURIComponent(board as string) : undefined;

  const [posts, setPosts] = useState<Post[]>([]);
  const [boardInfo, setBoardInfo] = useState<BoardInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [boardLoading, setBoardLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // postImages: post id를 key로, 해당 첨부파일의 URL을 저장
  const [postImages, setPostImages] = useState<{ [key: string]: string }>({});

  // Create client instance using the utility
  const supabase = createClient();

  // 게시글 가져오기 (전체 게시글 가져오도록 수정)
  useEffect(() => {
    async function fetchPosts() {
      if (!decodedBoard) return; // board 정보 없으면 실행 안함
      try {
        // 현재 게시판(category)의 글만 가져옴
        const fetchedPosts = await getPosts({ category: decodedBoard, limit: 100 }); // 필요시 페이징 구현
        setPosts(fetchedPosts);
      } catch (err) {
        console.error(err);
        setError("게시글 목록을 불러오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    }
    if (!boardLoading && boardInfo) { // board 정보 로딩 완료 후 실행
      fetchPosts();
    }
  }, [decodedBoard, boardLoading, boardInfo]); // boardLoading, boardInfo 추가

  // Supabase chn_list에서 board 정보 가져오기
  useEffect(() => {
    async function fetchBoardInfo() {
      if (!decodedBoard) {
        // 자유 게시판 기본 설정 제거 (URL 파라미터 필수)
        setError("게시판을 찾을 수 없습니다.");
        setBoardLoading(false);
        return;
      }
      setBoardLoading(true);
      try {
        const { data, error: fetchError } = await supabase
          .from("chn_list")
          .select("*")
          .eq("id", decodedBoard)
          .single();
        if (fetchError || !data) {
          setError("존재하지 않는 게시판");
        } else {
          setBoardInfo({
            id: data.id,
            title: data.title,
            type: data.type,
            permission: data.permission,
          });
        }
      } catch (err) {
        console.error(err);
        setError("게시판 정보를 불러오는 중 오류가 발생했습니다.");
      } finally {
        setBoardLoading(false);
      }
    }
    fetchBoardInfo();
  }, [decodedBoard, supabase]);

  // 갤러리/웹진 첨부파일 URL 불러오기
  useEffect(() => {
    async function fetchPostImages() {
      if (!boardInfo || (boardInfo.type !== "gallery" && boardInfo.type !== "webzine")) return;
      if (posts.length === 0) return;

      const postIdsToFetch = posts.map(p => p.id);
      if (postIdsToFetch.length === 0) return;

      const images: { [key: string]: string } = {};
      try {
        const { data, error: fetchError } = await supabase
          .from("bbs_file")
          .select("post_id, file_url")
          .in("post_id", postIdsToFetch);

        if (fetchError) {
          console.error("Error fetching images:", fetchError);
        } else if (data) {
          data.forEach(file => {
            if (!images[file.post_id]) {
              images[file.post_id] = file.file_url;
            }
          });
          setPostImages(images);
        }
      } catch (fetchError) {
        console.error("Error fetching images catch:", fetchError);
      }
    }

    if (!loading && posts.length > 0) { // 게시글 로딩 완료 후 실행
      fetchPostImages();
    }
  }, [boardInfo, posts, supabase, loading]); // loading 추가

  // 로딩 상태
  if (boardLoading) { // 게시판 정보 로딩 중
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center">
         <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 text-center text-red-500">
        <h1 className="text-2xl font-semibold mb-4">오류</h1>
        <p>{error}</p>
        <Button onClick={() => window.history.back()} className="mt-4">뒤로가기</Button>
      </div>
    );
  }

  // 렌더링 컴포넌트: boardInfo.type 값에 따라 다른 레이아웃
  const renderPosts = () => {
    if (!boardInfo) return null;

    // 게시글 로딩 중 스켈레톤 UI
    if (loading) {
       const skeletonCount = boardInfo.type === 'table' ? 10 : (boardInfo.type === 'gallery' ? 5 : 6);
       const skeletonGridClasses = boardInfo.type === 'gallery'
            ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
            : boardInfo.type === 'webzine'
            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            : 'grid-cols-1';
       return (
         <div className={`grid gap-4 md:gap-6 ${skeletonGridClasses}`}> 
           {Array.from({ length: skeletonCount }).map((_, idx) => (
             <Card key={idx} className="bg-gray-100 dark:bg-gray-800 animate-pulse">
               <CardContent className="p-4">
                 <div className={`h-${boardInfo.type === 'gallery' ? '48' : '32'} bg-gray-300 dark:bg-gray-700 rounded mb-3`}></div>
                 <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                 <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded w-1/2"></div>
               </CardContent>
             </Card>
           ))}
         </div>
       );
    }

    if (posts.length === 0) {
        return <p className="text-center text-gray-500 dark:text-gray-400 mt-8">게시글이 없습니다.</p>;
    }

    switch (boardInfo.type) {
      case "gallery":
        return (
          <div className="relative group/swiper">
            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={16}
              slidesPerView={2}
              navigation={{
                 nextEl: '.swiper-button-next-custom',
                 prevEl: '.swiper-button-prev-custom',
                 disabledClass: 'opacity-30 cursor-not-allowed',
              }}
              pagination={{ clickable: true }}
              breakpoints={{
                768: {
                  slidesPerView: 3,
                  spaceBetween: 20,
                },
                1024: {
                  slidesPerView: 4,
                  spaceBetween: 24,
                },
                1280: {
                  slidesPerView: 5,
                  spaceBetween: 24,
                },
              }}
              className="pb-10"
            >
              {posts.map((post) => (
                <SwiperSlide key={post.id} className="h-auto pb-1">
                  <Link
                    href={`/bbs/${boardInfo.id}/${post.id}`}
                    className="block group relative transform transition-transform duration-300 hover:scale-105 h-full"
                  >
                    <Card className="overflow-hidden h-full game-card bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md flex flex-col">
                      <CardContent className="p-0 flex-shrink-0">
                        <div className="relative h-48 w-full overflow-hidden">
                          {postImages[post.id] ? (
                            <Image
                              src={postImages[post.id]}
                              alt={post.title}
                              fill
                              className="object-cover transition-transform duration-500 group-hover:scale-110"
                              sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, (max-width: 1280px) 25vw, 20vw"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400 text-sm">
                              No Image
                            </div>
                          )}
                        </div>
                      </CardContent>
                      <div className="p-3 border-t dark:border-gray-700 mt-auto">
                        <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors" title={post.title}>
                          {post.title}
                        </h3>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex justify-between">
                          <span>{post.author_name || "익명"}</span>
                          <span>{new Date(post.created_at!).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
            <button className="swiper-button-prev-custom absolute top-1/2 left-0 md:-left-4 z-10 transform -translate-y-1/2 p-2 bg-white/70 dark:bg-black/50 hover:bg-white dark:hover:bg-black/70 rounded-full shadow-md opacity-0 group-hover/swiper:opacity-100 transition-opacity duration-300 cursor-pointer">
               <ChevronLeft className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            </button>
            <button className="swiper-button-next-custom absolute top-1/2 right-0 md:-right-4 z-10 transform -translate-y-1/2 p-2 bg-white/70 dark:bg-black/50 hover:bg-white dark:hover:bg-black/70 rounded-full shadow-md opacity-0 group-hover/swiper:opacity-100 transition-opacity duration-300 cursor-pointer">
               <ChevronRight className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            </button>
          </div>
        );
      case "webzine":
        return (
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => (
              <Link
                key={post.id}
                href={`/bbs/${boardInfo.id}/${post.id}`}
                className="block group"
              >
                <Card className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-lg transition overflow-hidden border dark:border-gray-700">
                  {postImages[post.id] && (
                    <div className="relative h-48 w-full flex-shrink-0">
                      <Image
                        src={postImages[post.id]}
                        alt={post.title}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                         sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      />
                    </div>
                  )}
                  <CardContent className="p-4 flex-grow flex flex-col justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-orange-700 dark:text-orange-400 group-hover:text-orange-600 dark:group-hover:text-orange-300 transition-colors mb-2 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 mb-3">
                        {removeScriptTags(post.content)} {/* 스크립트 태그 제거 */}
                      </p>
                    </div>
                    <div className="mt-auto text-xs text-gray-500 dark:text-gray-400 flex justify-between items-center pt-3 border-t dark:border-gray-700">
                      <span>{post.author_name || "익명"} · {new Date(post.created_at!).toLocaleDateString()}</span>
                      <div className="flex items-center space-x-2">
                         <span className="flex items-center"><ThumbsUp size={12} className="mr-1"/> {post.likes || 0}</span>
                         <span className="flex items-center"><Eye size={12} className="mr-1"/> {post.views || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        );
      case "table":
      default:
        return (
          <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                    제목
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                    작성자
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                    작성일
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                    <ThumbsUp size={14} className="inline-block"/>
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                    <Eye size={14} className="inline-block"/>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {posts.map((post) => (
                  <tr key={post.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link
                        href={`/bbs/${boardInfo.id}/${post.id}`}
                        className="text-sm font-medium text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 hover:underline truncate block max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg"
                         title={post.title}
                      >
                        {post.title}
                      </Link>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {post.author_name || "익명"}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(post.created_at!).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                      {post.likes || 0}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-400">
                       {post.views || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              {boardInfo?.title || "게시판"}
            </h1>
            {/* 게시판 설명 등 추가 정보 표시 가능 */}
        </div>
        {/* 글쓰기 버튼 */} 
        {boardInfo && ( 
          <Link href={`/bbs/${decodedBoard}/new`} className="mt-4 sm:mt-0">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white shadow-md">
              <Pencil className="w-4 h-4 mr-2" />
              글쓰기
            </Button>
          </Link>
        )}
      </header>

      <div className="mt-6">
        {renderPosts()}
      </div>

      {/* 페이지네이션 컴포넌트 추가 위치 */}
      {/* <Pagination /> */}

    </div>
  );
}
